import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Grid, IconButton, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { useObjectMenu } from "../useObjectMenu";
import TextCardEditor from "./TextCardEditor";
import TextCardStore from "./TextCardStore";

export const TextCardMenu = observer(({ store }: { store: TextCardStore }) => {
    const currentDashboard = useCurrentDashboard();
    const messageStore = useStore(MessageStore);
    const theme = useTheme();
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isDeleteLoading, setDeleteIsLoading] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [localText, setLocalText] = useState<string>(store.cardText ?? "");
    const {
        menuAnchor,
        isMenuOpen,
        setIsMenuOpen,
        handleMenuButtonClick,
        handleMenuButtonKeyDown,
    } = useObjectMenu();

    useEffect(() => {
        setLocalText(store.cardText ?? "");
    }, [store.cardText]);

    const handleDelete = async () => {
        if (!currentDashboard) {
            messageStore.logError("Failed to delete text card");
            return;
        }
        try {
            setDeleteIsLoading(true);
            await currentDashboard.deleteSignalsObjectById(store.id);
            messageStore.logMessage("Text card deleted", "success");
            setIsConfirmDeleteOpen(false);
        } catch (e) {
            messageStore.logError("Failed to delete text card");
        } finally {
            setDeleteIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!currentDashboard) {
            messageStore.logError("Failed to update text card");
            return;
        }
        try {
            setIsEditLoading(true);
            // Optimistically update local state
            store.setCardText(localText);
            await currentDashboard.updateTextCardText(store.id, localText);
            setIsEditOpen(false);
        } catch (e) {
            messageStore.logError("Failed to update text card");
        } finally {
            setIsEditLoading(false);
        }
    };

    const canConfigure = store.isEditable;
    const canDelete = store.isDeletable;

    if (!currentDashboard || (!canConfigure && !canDelete)) {
        return null;
    }

    return (
        <>
            <IconButton
                ref={menuAnchor}
                id={`${store.id}-text-card-menu`}
                style={{
                    color: theme.palette.black.main,
                    height: "34px",
                }}
                onClick={handleMenuButtonClick}
                onKeyDown={handleMenuButtonKeyDown}
                size="small"
            >
                <MoreVertIcon />
            </IconButton>
            <AcxMenu
                open={isMenuOpen}
                anchorElement={isMenuOpen ? menuAnchor.current : undefined}
                onMenuClose={() => setIsMenuOpen(false)}
                disableAutoFocusItem
                menuItems={[
                    ...(canConfigure
                        ? [
                              {
                                  id: `${store.id}-edit-text-card`,
                                  label: <StyledMenuLabel>Edit</StyledMenuLabel>,
                                  props: {
                                      onClick: () => {
                                          setIsEditOpen(true);
                                          setIsMenuOpen(false);
                                      },
                                  },
                              } as const,
                          ]
                        : []),
                    ...(canConfigure && canDelete ? (["Divider"] as const) : []),
                    ...(canDelete
                        ? [
                              {
                                  id: `${store.id}-delete-text-card`,
                                  label: <StyledMenuLabel>Delete</StyledMenuLabel>,
                                  props: {
                                      onClick: () => {
                                          setIsConfirmDeleteOpen(true);
                                          setIsMenuOpen(false);
                                      },
                                  },
                              } as const,
                          ]
                        : []),
                ]}
            />
            <AcxDialog
                isOpen={isConfirmDeleteOpen}
                onClose={() => setIsConfirmDeleteOpen(false)}
                title="Confirm Delete Text Card"
                dialogContentChildren={
                    <Box>Are you sure you want to delete this text card?</Box>
                }
                maxWidth={false}
            >
                <AcxButton onClick={handleDelete} loading={isDeleteLoading}>
                    Delete
                </AcxButton>
            </AcxDialog>
            <AcxDialog
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                title="Edit Text Card"
                dialogContentChildren={
                    <Grid container width={"80vw"}>
                        <Grid item xs={12}>
                            <TextCardEditor
                                showToolbar={isEditOpen}
                                text={localText}
                                setText={(input) => setLocalText(input)}
                                showClear
                            />
                        </Grid>
                    </Grid>
                }
                maxWidth={false}
            >
                <AcxButton onClick={handleSave} loading={isEditLoading}>
                    Save
                </AcxButton>
            </AcxDialog>
        </>
    );
});
